import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useReportTabs } from '@/contexts/ReportTabsContext';
import { resolveWorkspaceTab } from '@/lib/workspaceTabs';
import { api } from '@/lib/api';
import { GitBranch, FileText, Search, CornerDownLeft, RotateCcw } from 'lucide-react';

const TYPE_ICON = { model: GitBranch, insight: FileText, explore: Search };
const SECTION_LABEL = { saved: 'Saved insights & models', new: 'Create new', reopen: 'Recently closed' };

// Quick switcher: fuzzy-jump between open Workbench sessions, search all saved
// insights & models, and launch a new insight / model / explore. Opened by the
// dock "Open" button (⌘K is owned by the global Command Palette).
export default function WorkbenchSwitcher() {
  const ws = useWorkspace();
  const rt = useReportTabs();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [idx, setIdx] = useState(0);
  const [saved, setSaved] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onOpen = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('workbench:open-switcher', onOpen);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('workbench:open-switcher', onOpen); };
  }, []);

  useEffect(() => {
    if (open) { setQ(''); setIdx(0); setTimeout(() => inputRef.current?.focus(), 30); }
  }, [open]);

  // Load saved insights + models once the switcher opens so typing can search
  // across everything, not just the sessions already open.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    Promise.all([
      api.getReports().catch(() => []),
      api.getSemanticModels().catch(() => []),
    ]).then(([reports, models]) => {
      if (!alive) return;
      const rs = (reports || []).map(r => ({
        key: `insight:${r.report_id}`, type: 'insight',
        title: r.name || 'Untitled insight', path: `/dashboards/builder?id=${r.report_id}`, sub: 'Insight',
      }));
      const ms = (models || []).map(m => ({
        key: `model:${m.model_id}`, type: 'model',
        title: m.name || 'Untitled model', path: `/semantic-models?model=${m.model_id}`, sub: 'Model',
      }));
      setSaved([...rs, ...ms]);
    });
    return () => { alive = false; };
  }, [open]);

  const currentKey = resolveWorkspaceTab(location.pathname, location.search)?.key;

  const items = useMemo(() => {
    const sessions = (ws?.tabs || []).map(t => ({
      kind: 'session', key: t.key, type: t.type, title: t.title, path: t.path,
      sub: t.type === 'model' ? 'Model' : t.type === 'explore' ? 'AI Explore' : 'Insight',
    }));
    const creators = [
      { kind: 'new', key: 'new:insight', type: 'insight', title: 'New Insight', path: '/dashboards/builder?new=1', sub: 'Start a new Insight' },
      { kind: 'new', key: 'new:model', type: 'model', title: 'New / open a Model', path: '/semantic-models', sub: 'Open Models' },
      { kind: 'new', key: 'new:explore', type: 'explore', title: 'New AI Explore', path: '/ai-copilot', sub: 'Open AI Explore' },
    ];
    const openKeysAll = new Set((ws?.tabs || []).map(t => t.key));
    const reopens = (rt?.closedTabs || [])
      .filter(c => !(c.reportId && openKeysAll.has(`insight:${c.reportId}`)))
      .map(c => ({ kind: 'reopen', key: `reopen:${c.id}`, type: 'insight', title: c.label || 'New Insight', sub: c.hasDraft ? 'Recently closed · draft' : 'Recently closed', _entry: c }));
    const needle = q.trim().toLowerCase();
    if (!needle) return [...sessions, ...reopens, ...creators];
    const openKeys = new Set(sessions.map(s => s.key));
    const savedMatches = saved
      .filter(s => !openKeys.has(s.key) && s.title.toLowerCase().includes(needle))
      .slice(0, 8)
      .map(s => ({ ...s, kind: 'saved' }));
    const fSessions = sessions.filter(i => `${i.title} ${i.sub}`.toLowerCase().includes(needle));
    const fReopens = reopens.filter(i => `${i.title} ${i.sub}`.toLowerCase().includes(needle));
    const fCreators = creators.filter(i => `${i.title} ${i.sub}`.toLowerCase().includes(needle));
    return [...fSessions, ...savedMatches, ...fReopens, ...fCreators];
  }, [ws?.tabs, q, saved, rt?.closedTabs]);

  useEffect(() => { setIdx(i => Math.min(i, Math.max(0, items.length - 1))); }, [items.length]);

  if (!open) return null;

  const go = (item) => {
    setOpen(false);
    if (item?.kind === 'reopen') {
      rt?.reopenClosed?.(item._entry);
      navigate(item._entry?.reportId ? `/dashboards/builder?id=${item._entry.reportId}` : '/dashboards/builder');
      return;
    }
    if (item?.path) navigate(item.path);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setIdx(i => Math.min(i + 1, items.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); go(items[idx]); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center bg-black/50 backdrop-blur-sm p-4 pt-[12vh]" onClick={() => setOpen(false)} data-testid="workbench-switcher">
      <div className="w-full max-w-lg rounded-2xl border border-border/60 bg-card shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-4 border-b border-border/50">
          <Search className="w-4 h-4 text-muted-foreground shrink-0" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search insights & models, or switch sessions…"
            className="flex-1 bg-transparent py-3.5 text-sm text-foreground outline-none placeholder:text-muted-foreground/80"
            data-testid="workbench-switcher-input"
          />
        </div>
        <div className="max-h-[50vh] overflow-auto py-1.5">
          {items.length === 0 && <div className="px-4 py-6 text-xs text-muted-foreground text-center">No matches</div>}
          {items.map((item, i) => {
            const Icon = item.kind === 'reopen' ? RotateCcw : (TYPE_ICON[item.type] || Search);
            const activeRow = i === idx;
            const isCurrent = item.kind === 'session' && item.key === currentKey;
            const showDivider = (item.kind === 'saved' || item.kind === 'new' || item.kind === 'reopen') && i > 0 && items[i - 1].kind !== item.kind;

            return (
              <React.Fragment key={`${item.kind}-${item.key}`}>
                {showDivider && (
                  <div className="px-4 pt-2 pb-1" data-testid={`workbench-switcher-divider-${item.kind}`}>
                    <div className="border-t border-border/50" />
                    <div className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground/70 pt-1.5">{SECTION_LABEL[item.kind]}</div>
                  </div>
                )}
                <button
                  onMouseEnter={() => setIdx(i)}
                  onClick={() => go(item)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${activeRow ? 'bg-primary/10' : 'hover:bg-muted/30'}`}
                  data-testid={`workbench-switcher-item-${item.key}`}
                >
                  <Icon className={`w-4 h-4 shrink-0 ${activeRow ? 'text-primary' : 'text-muted-foreground'}`} strokeWidth={1.5} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-foreground truncate">{item.title}</div>
                    <div className="text-[10px] text-muted-foreground/80">{item.kind === 'session' ? `${item.sub}${isCurrent ? ' · current' : ''}` : item.sub}</div>
                  </div>
                  {activeRow && <CornerDownLeft className="w-3.5 h-3.5 text-muted-foreground/70 shrink-0" />}
                </button>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}
